import {
  type Identifier,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
} from "ra-core";

import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { Task } from "../types";

import { EditSheet } from "../misc/EditSheet";

import { TaskFormContent } from "./TaskFormContent";

type TaskContactLink = {
  id: Identifier;
  task_id: Identifier;
  contact_id: Identifier;
  created_at?: string;
};

export interface TaskEditSheetProps {
  open: boolean;

  onOpenChange: (open: boolean) => void;

  taskId: Identifier;
}

export const TaskEditSheet = ({
  open,
  onOpenChange,
  taskId,
}: TaskEditSheetProps) => {
  const dataProvider = useDataProvider();

  const notify = useNotify();

  const queryClient = useQueryClient();

  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    [],
  );

  const [initialized, setInitialized] = useState(false);

  /*
   * Получаем саму задачу.
   * Старый contact_id пока нужен
   * для обратной совместимости.
   */
  const { data: task, isPending: isTaskPending } = useGetOne<Task>(
    "tasks",
    {
      id: taskId,
    },
    {
      enabled: open && taskId != null,
    },
  );

  /*
   * Получаем все контакты,
   * связанные с задачей
   * через task_contacts.
   */
  const {
    data: taskContacts = [],
    isPending: isTaskContactsPending,
    refetch: refetchTaskContacts,
  } = useGetList<TaskContactLink>(
    "task_contacts",
    {
      pagination: {
        page: 1,
        perPage: 1000,
      },

      sort: {
        field: "created_at",
        order: "ASC",
      },

      filter: {
        task_id: taskId,
      },
    },
    {
      enabled: open && taskId != null,
    },
  );

  /*
   * После закрытия очищаем
   * локальное состояние.
   *
   * При следующем открытии
   * контакты загрузятся заново.
   */
  useEffect(() => {
    if (open) {
      return;
    }

    setInitialized(false);

    setSelectedContactIds([]);
  }, [open, taskId]);

  /*
   * После загрузки задачи
   * и task_contacts формируем
   * текущий список контактов.
   */
  useEffect(() => {
    if (
      !open ||
      initialized ||
      isTaskPending ||
      isTaskContactsPending ||
      !task
    ) {
      return;
    }

    const ids = new Map<string, Identifier>();

    /*
     * Новая модель.
     */
    for (const relation of taskContacts) {
      ids.set(String(relation.contact_id), relation.contact_id);
    }

    /*
     * Если это старая задача
     * и её contact_id ещё
     * почему-то отсутствует
     * в task_contacts,
     * не теряем его.
     */
    if (task.contact_id != null) {
      ids.set(String(task.contact_id), task.contact_id);
    }

    setSelectedContactIds(Array.from(ids.values()));

    setInitialized(true);
  }, [
    open,
    initialized,
    isTaskPending,
    isTaskContactsPending,
    task,
    taskContacts,
  ]);

  /*
   * Синхронизация выбранных
   * контактов с task_contacts.
   */
  const syncTaskContacts = async () => {
    const wantedIds = new Set(selectedContactIds.map((id) => String(id)));

    const existingIds = new Set(
      taskContacts.map((relation) => String(relation.contact_id)),
    );

    /*
     * Новые контакты,
     * которых ещё нет в БД.
     */
    const idsToCreate = selectedContactIds.filter(
      (contactId) => !existingIds.has(String(contactId)),
    );

    /*
     * Связи, которые
     * пользователь удалил.
     */
    const linksToDelete = taskContacts.filter(
      (relation) => !wantedIds.has(String(relation.contact_id)),
    );

    const operations: Promise<unknown>[] = [];

    for (const contactId of idsToCreate) {
      operations.push(
        dataProvider.create("task_contacts", {
          data: {
            task_id: taskId,

            contact_id: contactId,
          },
        }),
      );
    }

    for (const relation of linksToDelete) {
      operations.push(
        dataProvider.delete("task_contacts", {
          id: relation.id,

          previousData: relation,
        }),
      );
    }

    if (operations.length > 0) {
      await Promise.all(operations);
    }

    await refetchTaskContacts();

    /*
     * Обновляем все места,
     * где могут отображаться
     * задача и контакты.
     */
    await queryClient.invalidateQueries({
      queryKey: ["task_contacts"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["contact_tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["contacts"],
    });
  };

  /*
   * EditSheet вызывает этот callback
   * после успешного сохранения
   * самой задачи.
   */
  const handleSuccess = async () => {
    try {
      await syncTaskContacts();

      notify("Задача обновлена", {
        type: "success",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to sync task contacts", error);

      /*
       * Основная задача к этому
       * моменту уже сохранена.
       */
      notify("Задача сохранена, но возникла ошибка при обновлении контактов", {
        type: "warning",
      });

      onOpenChange(false);
    }
  };

  /*
   * Пока данные загружаются,
   * EditSheet не открываем.
   *
   * Это не даёт форме сначала
   * показать пустой список контактов,
   * а затем внезапно заменить его.
   */
  if (open && (isTaskPending || isTaskContactsPending || !initialized)) {
    return null;
  }

  return (
    <EditSheet
      resource="tasks"
      id={taskId}
      title="Редактировать задачу"
      redirect={false}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: handleSuccess,
      }}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TaskFormContent
        selectedContactIds={selectedContactIds}
        onSelectedContactIdsChange={setSelectedContactIds}
      />
    </EditSheet>
  );
};
